import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useAuth } from "./AuthContext";

/**
 * Backend-backed feeds: creators room, news, chat.
 * Reads are open; writes require a JWT (added via AuthContext).
 */
const FeedContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

async function apiGet(path) {
  try {
    const r = await fetch(`${API}${path}`, { credentials: "include" });
    if (!r.ok) return null;
    return await r.json();
  } catch {
    return null;
  }
}

async function apiPost(path, body, token) {
  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers,
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) {
    const message = Array.isArray(data?.detail)
      ? data.detail[0]?.msg || "Something went wrong."
      : data?.detail || "Something went wrong.";
    return { ok: false, error: message };
  }
  return { ok: true, data };
}

export const FeedProvider = ({ children }) => {
  const { token } = useAuth();
  const [creators, setCreators] = useState([]);
  const [news, setNews] = useState([]);
  const [chat, setChat] = useState([]);

  const refreshCreators = useCallback(async () => {
    const data = await apiGet("/api/feeds/creators");
    if (data?.posts) setCreators(data.posts);
  }, []);
  const refreshNews = useCallback(async () => {
    const data = await apiGet("/api/feeds/news");
    if (data?.posts) setNews(data.posts);
  }, []);
  const refreshChat = useCallback(async () => {
    const data = await apiGet("/api/feeds/chat");
    if (data?.messages) setChat(data.messages);
  }, []);

  useEffect(() => {
    refreshCreators();
    refreshNews();
    refreshChat();
  }, [refreshCreators, refreshNews, refreshChat]);

  const postCreator = useCallback(
    async ({ title, body }) => {
      const r = await apiPost("/api/feeds/creators", { title, body }, token);
      if (r.ok) setCreators((p) => [r.data.post, ...p]);
      return r;
    },
    [token]
  );

  const postNews = useCallback(
    async ({ title, body, link }) => {
      const r = await apiPost("/api/feeds/news", { title, body, link }, token);
      if (r.ok) setNews((p) => [r.data.post, ...p]);
      return r;
    },
    [token]
  );

  const postChat = useCallback(
    async ({ text }) => {
      const r = await apiPost("/api/feeds/chat", { text }, token);
      if (r.ok) setChat((p) => [...p, r.data.message]);
      return r;
    },
    [token]
  );

  const value = useMemo(
    () => ({
      creators,
      news,
      chat,
      postCreator,
      postNews,
      postChat,
      refreshCreators,
      refreshNews,
      refreshChat,
    }),
    [creators, news, chat, postCreator, postNews, postChat, refreshCreators, refreshNews, refreshChat]
  );

  return <FeedContext.Provider value={value}>{children}</FeedContext.Provider>;
};

export const useFeed = () => {
  const ctx = useContext(FeedContext);
  if (!ctx) throw new Error("useFeed must be inside FeedProvider");
  return ctx;
};
